import { Stack } from '@mui/joy';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { FormField } from 'core/components';
import { InputType, LabelPosition, SortDirection } from 'core/enums';
import { useFormContext } from 'core/states';
import { partService } from 'parts/services';
import { IPartHistoryRequestPayload } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';
import { Direction, Size, Variant } from 'theme/enums';

import { usePriceFormFieldsContext } from './state';

interface IProps {
  disabled: boolean;
}

export const UseLatestQuotePrice = ({ disabled }: IProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const { t } = useTranslation();

  const { setValue, watch } = useFormContext();

  const { onChangePrice, quote } = usePriceFormFieldsContext();

  const partValue = watch('part');
  const partNumber: string | undefined = partValue?.number;
  const manufacturerId: number | undefined = partValue?.manufacturerId;
  const customerContactGroupName = quote?.customerContactGroup?.name;
  const quoteId = quote?.id;

  const requestPayload = useMemo<IPartHistoryRequestPayload | null>(() => {
    if (disabled || !partNumber || !manufacturerId || !customerContactGroupName || !quoteId) {
      return null;
    }

    return {
      filters: {
        partNumber,
        manufacturerId,
        customerContactGroupName,
        excludeQuoteId: quoteId,
      },
      page: 1,
      pageSize: 1,
      sortField: ['updated_at'],
      sortDir: [SortDirection.DESC],
    };
  }, [disabled, partNumber, manufacturerId, customerContactGroupName, quoteId]);

  const { data } = useQuery(
    ['latestQuotePrice', requestPayload],
    ({ signal }) => partService.getPartHistory(requestPayload!, signal),
    { enabled: !!requestPayload }
  );

  const latestPrice = useMemo(() => {
    const record = data?.records?.[0];

    return record?.price ?? null;
  }, [data]);

  const priceValue = watch('price');

  useEffect(() => {
    if (isChecked && Number(latestPrice) !== Number(priceValue)) {
      setIsChecked(false);
    }
  }, [isChecked, latestPrice, priceValue]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIsChecked(event.target.checked);

      if (event.target.checked && latestPrice != null) {
        setValue('price', latestPrice, { shouldValidate: true });
        onChangePrice({ source: 'event', target: { value: latestPrice } });
      }
    },
    [latestPrice, setValue, onChangePrice]
  );

  if (latestPrice == null || disabled) {
    return null;
  }

  return (
    <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
      <FormField
        name="useLatestQuotePrice"
        type={InputType.SWITCH}
        inputProps={{
          checked: isChecked,
          label: t('labelUseLatestPrice'),
          labelPosition: LabelPosition.END,
          size: Size.MD,
          variant: isChecked ? Variant.SOFT : Variant.SOLID,
          onChange: onChange,
        }}
      />

      <FormField
        name="latestQuotePrice"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          value: PricingHelper.formatPricing({ amount: latestPrice }),
          disabled: true,
          sx: { width: 'auto', maxWidth: 120 },
          slotProps: {
            input: {
              prefix: '$',
            },
          },
        }}
      />
    </Stack>
  );
};
